import React, { useState } from 'react';
import styled from 'styled-components';

import { Grid, Button, Chip, ButtonProps, ButtonGroup, LinearProgress } from '@material-ui/core';

// Icons
import Icon from '@mdi/react';
import { mdiMenuUp, mdiMenuDown, mdiMenuLeft, mdiMenuRight, mdiCrosshairs } from '@mdi/js';

import { colors, din, easeTransition, buttonShadow, pulseHalf } from '../../styles/variables';
import { px, fade } from '../../util/style';
import without from '../../util/without';

import { ControlLabel } from './Labels';
import { LightTooltip } from './Tooltips';

//
// PushButton
//
interface PushButtonProps extends ButtonProps {
  marginLeft?: string;
  pulse?: boolean;
  icon?: boolean;
  small?: boolean;
  round?: boolean;
  noBorder?: boolean;
  primary?: boolean;
  secondary?: boolean;
  white?: boolean;
  midGray?: boolean;
  blue?: boolean;
  yellow?: boolean;
  emergency?: boolean;
  error?: boolean;
  blueBG?: boolean;
  redBG?: boolean;
  transparent?: boolean;
  forceColor?: string;
  forceBgColor?: string;
}
export const PushButton = styled(Button).withConfig({
  shouldForwardProp: (prop, defaultValidatorFn) =>
    ![
      'pulse',
      'icon',
      'small',
      'round',
      'noBorder',
      'white',
      'midGray',
      'blue',
      'yellow',
      'emergency',
      'error',
      'redBG',
      'blueBG',
      'primary',
      'secondary',
      'transparent',
      'forceColor',
      'forceBgColor',
      'marginLeft'
    ].includes(prop as any) && defaultValidatorFn(prop)
})<PushButtonProps>`
  font-family: ${din};
  font-weight: 400;
  padding: ${px(14)} ${px(36)} ${px(14)} ${px(28)};
  margin: 0 ${px(12)};
  text-align: center;
  transition: all ${easeTransition};
  margin-left: ${props => (props.marginLeft ? `${props.marginLeft} !important` : 0)};

  &:active {
    transform: scale(0.95);
  }

  &:first-of-type {
    margin-left: 0;
  }

  &:last-of-type {
    margin-right: 0;
  }

  & span {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  & p {
    margin: 0;
    padding: 0;
  }

  & svg {
    width: ${px(24)};
    height: ${px(24)};
    margin-right: ${px(8)};
  }

  ${props =>
    props.hidden &&
    `
    visibility: hidden;
  `}

  ${props =>
    props.disabled &&
    `
    opacity: 0.5;
  `}

  ${props =>
    props.pulse &&
    `
    ${pulseHalf}

    animation: 1s infinite alternate pulseHalf;
    animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  `}

  ${props =>
    props.icon &&
    `
    min-width: 0;
    width: ${px(56)};
    height: ${px(56)};
    padding: ${px(8)} 0;
    margin: 0;

    & svg {
      margin: 0;
      width: ${px(24)};
      height: ${px(24)};
    }
  `}

  ${props =>
    !props.icon &&
    `
    padding: ${px(14)} ${px(36)};
  `}

  ${props =>
    props.small &&
    `
    height: ${px(42)};
    padding: ${px(8)} ${px(24)} ${px(8)} ${px(16)};

    & svg {
      width: ${px(18)};
      height: ${px(18)};
    }
  `}

  ${props =>
    props.icon &&
    props.small &&
    `
    padding: 0;
    min-width: ${px(40)};
    max-width: ${px(40)};
    width: ${px(40)};
    height: ${px(40)};
  `}

  ${props =>
    props.round &&
    `
    border-radius: 100%;
  `}

  ${props =>
    props.noBorder &&
    `
    border: none !important;
  `}

  ${props =>
    props.primary &&
    `
    color: #fff !important;
    background-color: ${colors.emerald};
    border: 1px solid ${colors.emerald};

    &:hover {
      background-color: ${fade(colors.emerald, 0.25)};
    }

    & svg {
      color: #fff;
    }
  `}

  ${props =>
    props.secondary &&
    `
    color: ${colors.gray87};
    background-color: ${colors.milk2};
    border: 1px solid ${colors.gray7};

    &:hover {
      background-color: ${colors.gray7};
    }

    & svg {
      color: ${colors.gray87};
    }
  `}

  ${props =>
    props.white &&
    `
    color: ${colors.gray87};
    background-color: #fff;
    border: 1px solid ${colors.gray7};

    &:hover {
      background-color: ${fade(colors.rainLight, 0.85)};
    }

    & svg {
      color: ${colors.gray87};
    }
  `}

  ${props =>
    props.midGray &&
    `
    color: #fff;
    background-color: ${colors.gray45};
    border: 1px solid ${colors.gray45};

    &:hover {
      background-color: ${fade(colors.gray45, 0.25)};
    }

    & svg {
      color: #fff;
    }
  `}

  ${props =>
    props.blue &&
    `
    color: #fff !important;
    background-color: ${colors.rainLight};
    border: 1px solid ${colors.rainLight};

    &:hover {
      background-color: ${fade(colors.rainLight, 0.25)};
    }

    & svg {
      color: #fff;
    }
  `}

  ${props =>
    props.yellow &&
    `
    color: #fff;
    background-color: ${colors.deliMustard};
    border: 1px solid ${colors.deliMustard};

    &:hover {
      background-color: ${fade(colors.deliMustard, 0.25)};
    }

    & svg {
      color: #fff;
    }
  `}

  ${props =>
    props.emergency &&
    `
    color: #fff;
    background-color: ${colors.alleviRedDark};
    border: 1px solid ${colors.alleviRedDark};

    &:hover {
      background-color: ${fade(colors.alleviRedDark, 0.25)};
    }

    & svg {
      color: #fff;
    }
  `}

  ${props =>
    props.error &&
    `
    color: #fff;
    background-color: ${colors.errorClapton};
    border: 1px solid ${colors.errorClapton};

    &:hover {
      background-color: ${fade(colors.errorClapton, 0.25)};
    }

    & svg {
      color: #fff;
    }
  `}

  ${props =>
    props.blueBG &&
    `
    color: ${colors.rainLight};
    background-color: transparent;
    border: 1px solid transparent;
    transition: all 0.25s ease-in-out;

    &:hover {
      background-color: ${fade(colors.rainLight, 0.85)};
      border: 1px solid ${colors.rainLight};
    }

    & svg {
      color: ${colors.rainLight};
    }
  `}

  ${props =>
    props.redBG &&
    `
    color: ${colors.alleviRed};
    background-color: transparent;
    border: 1px solid transparent;
    transition: all 0.25s ease-in-out;

    &:hover {
      background-color: ${fade(colors.alleviRed, 0.85)};
      border: 1px solid ${colors.alleviRed};
    }

    & svg {
      color: ${colors.alleviRed};
    }
  `}

  ${props =>
    props.transparent &&
    `
    color: ${colors.gray87};
    background: transparent;
    border: none;

    & svg {
      color: ${colors.gray87};
    }
  `}

  ${props =>
    props.forceColor &&
    `
    color: ${props.forceColor};

    & svg {
      color: ${props.forceColor};
    }
  `};

  ${props =>
    props.forceBgColor &&
    `
    background-color: ${props.forceBgColor};
    border: 1px solid ${props.forceBgColor};

    &:hover {
      background-color: ${fade(props.forceBgColor, 0.25)};
      border: 1px solid ${props.forceBgColor};
    }
  `};
` as React.ComponentType<PushButtonProps>;

//
// MomentaryButton
//
interface MomentaryProps {
  haslabel?: boolean;
  active?: boolean;
}
const Momentary = styled(without('haslabel', 'active')(Chip))<MomentaryProps>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${colors.rainLight};
  width: ${px(32)};
  height: ${px(32)};
  margin: ${px(10)} ${px(8)};
  margin-right: ${px(16)};
  transition: all ${easeTransition};

  &:hover {
    cursor: pointer;
    background-color: ${fade(colors.rainLight, 0.15)};
  }

  ${props =>
    props.haslabel &&
    `
    margin-left: ${px(8)};
  `}

  ${props =>
    !props.haslabel &&
    `
    margin-top: ${px(8)};
  `}

  ${props =>
    props.active &&
    `
    background-color: ${colors.deliMustard};
    box-shadow: 0 0 ${px(12)} 0 ${fade(colors.deliMustard, 0.15)};

    &:hover {
      background-color: ${colors.deliMustard};
    }
  `}

  ${props =>
    props.disabled &&
    `
    background-color: ${colors.gray45} !important;

    &:hover {
      cursor: default;
    }
  `}
`;

interface MomentaryIconProps {
  active?: boolean;
  disabled?: boolean;
}
const MomentaryIcon = styled.div<MomentaryIconProps>`
  position: absolute;
  background-color: #fff;
  width: ${px(23)};
  height: ${px(23)};
  transition: all ${easeTransition};
  border-radius: 100%;
  margin: 0;
  transform-origin: center center;
  transform: scale(1);
  box-shadow: ${buttonShadow()};

  ${props =>
    props.active &&
    `
    transform: scale(0);
  `}

  ${props =>
    props.disabled &&
    `
    box-shadow: ${buttonShadow('#fff')};
  `}
`;

interface MomentaryButtonProps {
  label?: string;
  disabled?: boolean;
  checked?: boolean;
  onChange?: (v: boolean) => void;
}
export const MomentaryButton = (props: MomentaryButtonProps) => {
  const [active, setActive] = useState(false);

  const callOnChange = (v: boolean) => {
    if (typeof props.onChange === 'function') {
      props.onChange(v);
    }
  };

  const momentaryButton = (
    <Momentary
      disabled={props.disabled}
      active={active}
      haslabel={!props.label}
      onMouseDown={() => {
        if (!props.disabled) {
          setActive(true);
          callOnChange(true);
        }
      }}
      onMouseUp={() => {
        if (!props.disabled) {
          setActive(false);
          callOnChange(false);
        }
      }}
      onMouseLeave={() => {
        if (!props.disabled && active) {
          setActive(false);
          callOnChange(false);
        }
      }}
      icon={<MomentaryIcon active={active} disabled={props.disabled} />}
    />
  );

  return (
    <React.Fragment>
      {props.label ? (
        <ControlLabel label={props.label} labelPlacement="start" control={momentaryButton} />
      ) : (
        momentaryButton
      )}
    </React.Fragment>
  );
};

//
// DirectionalPad
//
interface GridCellProps {
  middle?: boolean;
  twoway?: boolean;
  direction?: 'up' | 'left' | 'right' | 'down';
}
const GridCell = styled(without('middle', 'direction', 'twoway')(Grid))<GridCellProps>`
  position: relative;
  height: 33.3%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  user-select: none;

  & svg {
    height: ${px(24)};
    color: #fff;
  }

  ${props =>
    props.middle &&
    props.twoway &&
    `
    border-left: 1px solid ${colors.gray13};
    border-right: 1px solid ${colors.gray13};
  `}

  ${props =>
    props.direction &&
    `
    background-color: ${colors.rainLight};
    border: 1px solid ${colors.gray13};
    transition: all ${easeTransition};

    &:hover {
      background-color: ${fade(colors.rainLight, 0.15)};
      cursor: pointer;
    }

    &:active {
      transform: scale(1.05);
    }
  `}

  ${props =>
    props.direction === 'up' &&
    `
    border-bottom: 1px solid #fff;

    & svg {
      margin-bottom: ${px(8)};
    }
  `}

  ${props =>
    props.direction === 'left' &&
    `
    border-right: 1.5px solid #fff;

    & svg {
      margin-right: ${px(8)};
    }
  `}

  ${props =>
    props.direction === 'right' &&
    `
    border-left: 1px solid #fff;

    & svg {
      margin-left: ${px(8)};
    }
  `}

  ${props =>
    props.direction === 'down' &&
    `
    border-top: 1px solid #fff;

    & svg {
      margin-top: ${px(8)};
    }
  `}
`;

interface CurveCellProps {
  direction?: 'up' | 'left' | 'right' | 'down';
}
const CurveCell = styled.div<CurveCellProps>`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 100;
  background-color: #fff;
  border-top-left-radius: 100%;

  ${props =>
    props.direction === 'up' &&
    `
    transform: rotate(45deg);
    bottom: -50%;
  `}

  ${props =>
    props.direction === 'left' &&
    `
    transform: rotate(-45deg);
    right: -50%;
  `}

  ${props =>
    props.direction === 'right' &&
    `
    transform: rotate(135deg);
    left: -50%;
  `}

  ${props =>
    props.direction === 'down' &&
    `
    transform: rotate(-135deg);
    top: -50%;
  `}
`;

interface DPadCellProps {
  middle?: boolean;
  twoway?: boolean;
  direction?: 'up' | 'left' | 'right' | 'down' | false;
  children?: React.ReactNode;
  onClick?: () => void;
}
export const DPadCell = (props: DPadCellProps) => {
  const renderDirectionIcon = () => {
    switch (props.direction) {
      case 'up':
        return <Icon path={mdiMenuUp} />;
      case 'down':
        return <Icon path={mdiMenuDown} />;
      case 'left':
        return <Icon path={mdiMenuLeft} />;
      case 'right':
        return <Icon path={mdiMenuRight} />;
      default:
        return false;
    }
  };

  const callOnClick = () => {
    if (typeof props.onClick === 'function') {
      props.onClick();
    }
  };

  return (
    <GridCell
      direction={props.direction || undefined}
      middle={props.middle}
      twoway={props.twoway}
      onClick={() => (props.direction ? callOnClick() : false)}
      item
      xs={4}
    >
      {props.direction && (
        <React.Fragment>
          {renderDirectionIcon()}
          <CurveCell direction={props.direction} />
        </React.Fragment>
      )}

      {props.children}
    </GridCell>
  );
};

const DPadContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
`;

const DPadGrid = styled(Grid)`
  width: ${px(120)};
  height: ${px(120)};
`;

const CellLabel = styled.span`
  font-family: ${din};
  font-weight: 600;
  color: ${colors.gray87};
`;

const ResetLabel = styled.div`
  text-align: center;
  font-weight: 600;
  font-size: ${px(10)};
  padding-top: ${px(5)};
  color: ${colors.gray45};
`;

interface DirectionalPadProps {
  label: string;
  middle?: boolean;
  twoway?: boolean;
  onMove?: (direction: 'left' | 'right' | 'up' | 'down') => void;
  onHome?: () => void;
  onHomeX?: () => void;
}
export const DirectionalPad = (props: DirectionalPadProps) => {
  const callOnMove = (direction: 'left' | 'right' | 'up' | 'down') => {
    if (typeof props.onMove === 'function') {
      props.onMove(direction);
    }
  };

  const callOnHome = () => {
    if (typeof props.onHome === 'function') {
      props.onHome();
    }
  };

  const callOnHomeX = () => {
    if (typeof props.onHomeX === 'function') {
      props.onHomeX();
    }
  };

  return (
    <DPadContainer>
      {/*//@ts-expect-error not sure if this is an actual error or a bad typing of DPadGrid*/}
      <DPadGrid container padding={0}>
        <DPadCell />
        <DPadCell direction="up" onClick={() => callOnMove('up')} />
        <DPadCell />

        <DPadCell
          direction={props.twoway ? false : 'left'}
          onClick={() => (props.twoway ? false : callOnMove('left'))}
        />
        <DPadCell middle twoway={props.twoway}>
          <CellLabel>{props.label}</CellLabel>
        </DPadCell>
        <DPadCell
          direction={props.twoway ? false : 'right'}
          onClick={() => (props.twoway ? false : callOnMove('right'))}
        />

        <DPadCell />
        <DPadCell direction="down" onClick={() => callOnMove('down')} />
        <DPadCell />
      </DPadGrid>

      <div style={{ height: `${px(24)}` }} />

      <div style={{ display: 'flex' }}>
        {props.twoway ? null : (
          <div style={{ marginRight: `${px(10)}` }}>
            <LightTooltip title="Move to axis calibration position.">
              <PushButton icon small secondary onClick={() => callOnHomeX()}>
                <Icon path={mdiCrosshairs} />
              </PushButton>
            </LightTooltip>
            <ResetLabel>X</ResetLabel>
          </div>
        )}
        <div>
          <LightTooltip title="Move to axis calibration position.">
            <PushButton icon small secondary onClick={() => callOnHome()}>
              <Icon path={mdiCrosshairs} />
            </PushButton>
          </LightTooltip>
          <ResetLabel>{props.twoway ? props.label : props.label.split('/')[1]}</ResetLabel>
        </div>
      </div>
    </DPadContainer>
  );
};

interface LightButtonProps {
  red?: boolean;
  worklightOn?: boolean;
}
export const LightButton = styled(without('red', 'worklightOn')(PushButton))<LightButtonProps>`
  position: absolute;
  right: ${px(32)};
  min-width: 0;
  width: ${px(42)};
  height: ${px(42)};
  color: ${colors.gray45};
  border: 1px solid ${colors.gray45};
  border-radius: 100%;
  padding: 0;
  margin: 0;
  transition: all 0.25s ease-in-out;

  & svg {
    height: ${px(32)};
    color: ${colors.gray45};
    margin: 0;
  }

  ${props =>
    props.red &&
    `
    border: 1px solid ${colors.alleviRedLight};

    &:hover {
      background-color: ${fade(colors.alleviRed, 0.85)};
    }

    & svg {
      color: ${colors.alleviRedLight};
    }
  `}

  ${props =>
    props.worklightOn &&
    `
    color: #fff;
    background-color: ${colors.deliMustard};
    border-color: ${colors.deliMustard};

    &:hover {
      background-color: ${colors.deliMustardLight};
    }

    & svg {
      color: #fff;
    }
  `}
`;

interface SegmentedButtonProps {
  active?: boolean;
  disabled?: boolean;
  alt?: boolean;
  small?: boolean;
  vertical?: boolean;
}

export const SegmentedButton = styled(Button)<SegmentedButtonProps>`
  font-family: ${din};
  font-weight: 500;
  padding: ${px(10)} ${px(10)} ${px(10)} ${px(10)};
  text-align: center;
  transition: all ${easeTransition};
  margin: 0px;
  color: #000;
  background-color: ${fade(colors.rainLight, 0.85)};
  min-width: ${px(100)};
  border: none;

  &:active {
    background-color: ${fade(colors.rainLight, 0.75)};
    transform: scale(1.05);
  }

  &:focus {
    background-color: ${fade(colors.rainLight, 0.85)};
  }

  &:hover {
    background-color: ${fade(colors.rainLight, 0.75)};
  }

  ${props =>
    props.alt &&
    `
    font-weight: 400;
    color: #000;
    background-color: ${fade(colors.deliMustard, 0.85)};

    &:hover {
      background-color: ${fade(colors.deliMustard, 0.75)};
    }

    &:focus {
      background-color: ${fade(colors.deliMustard, 0.85)};
    }

    &:active {
      background-color: ${fade(colors.deliMustard, 0.75)};
    }
  `}

  ${props =>
    props.alt &&
    props.active &&
    `
    font-weight: 600;
    color: #fff;
    background-color: ${colors.deliMustard};
    box-shadow: 0 0 ${px(8)} 0 ${fade(colors.deliMustard, 0.5)};

    &:hover {
      background-color: ${fade(colors.deliMustard, 0.2)};
    }

    &:focus {
      background-color: ${colors.deliMustard};
    }

    &:active {
      background-color: ${fade(colors.deliMustard, 0.2)};
    }
  `}

  ${props =>
    props.active &&
    `
    color: #fff;
    background-color: ${colors.rainLight};
    font-weight: 700;

    &:hover {
      background-color: ${fade(colors.rainLight, 0.25)};
    }

    &:focus {
      background-color: ${colors.rainLight};
    }

    &:active {
      background-color: ${fade(colors.rainLight, 0.2)};
    }
  `}

  ${props =>
    props.vertical &&
    `
    height: ${px(32)};
  `}

  ${props =>
    props.small &&
    `
    min-width: ${px(64)};

    &:not(:last-of-type) {
      border-right: none;
      border-bottom: 1px solid ${fade(colors.rainLight, 0.75)};
    }
  `}
`;

export const SegmentedButtonGroup = styled(ButtonGroup)`
  .Mui-disabled {
    border: 1px solid #efefee;
    background-color: ${colors.gray13};
    color: ${fade(colors.gray87, 0.5)};
  }
`;

export const LoadingProgress = styled(LinearProgress)`
  position: absolute;
  z-index: 2;
  top: 0;
  width: 100%;
  background-color: ${fade(colors.rainLight, 0.75)};

  .MuiLinearProgress-barColorPrimary {
    background-color: ${colors.rainLight};
  }
`;

export const ButtonLoadingWrapper = styled.div`
  position: relative;
`;
